import { movies, spotlightBanners, spotlightEditors } from "@/mocks/movies";
import type { Movie, SpotlightKey } from "@/pages/home/types";

interface SpotlightSectionProps {
  spotlightKey: SpotlightKey;
  sectionId: string;
  background: "base" | "alt";
  reverse?: boolean;
}

const allMovies = movies as Movie[];

export default function SpotlightSection({
  spotlightKey,
  sectionId,
  background,
  reverse = false,
}: SpotlightSectionProps) {
  const editor = spotlightEditors[spotlightKey];
  const picks = editor.picks
    .map((id) => allMovies.find((movie) => movie.id === id))
    .filter((movie): movie is Movie => Boolean(movie));

  return (
    <section
      id={sectionId}
      className={`scroll-mt-20 py-14 md:py-20 ${
        background === "alt" ? "bg-background-100" : "bg-background-50"
      }`}
    >
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
        <div className="relative h-56 w-full overflow-hidden rounded-lg md:h-72">
          <img
            src={spotlightBanners[spotlightKey]}
            alt=""
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-100">
              <i className="ri-movie-2-line" />
              九七电影院 · {editor.title}专区
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {editor.title}找片指南
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {editor.intro}
            </p>
          </div>
        </div>

        <div
          className={`mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5 lg:items-start ${
            reverse ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-lightbulb-line" />
                </span>
                观影提示
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{editor.note}</p>
            </div>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                为什么单独设「{editor.title}」专区
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                同一类型里的作品差异很大，单独成区可以更快对比它们的看点与适合的观看场景。
                如果你已经明确偏好，直接在这里挑一部开看即可；如果还在犹豫，再回到上方的类型片浏览器做交叉筛选。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-heading text-lg font-bold text-foreground-950">编辑推荐 · {editor.title}</h3>
            <ol className="mt-4 flex flex-col gap-3">
              {picks.map((movie) => (
                <li
                  key={movie.id}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3"
                >
                  <div className="h-28 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={movie.img}
                      alt={`《${movie.title}》风格封面`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <h4 className="font-heading text-base font-bold text-foreground-950">
                        {movie.title}
                      </h4>
                      <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
                        <i className="ri-star-fill" />
                        {movie.score}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500">
                      {movie.year} · {movie.region} · 导演 {movie.director}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{movie.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}